import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, CalendarClock, CheckCircle2, ClipboardCheck,
  Clock, Inbox, Loader2, RefreshCw, ShieldAlert, ShieldCheck, Zap,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_TONE = {
  low:       'bg-emerald-100 text-emerald-800',
  moderate:  'bg-amber-100 text-amber-800',
  high:      'bg-orange-100 text-orange-900',
  very_high: 'bg-red-100 text-red-900',
};

const OUTCOMES = [
  { v: 'no_readmit',        l: 'No readmit', tone: 'text-emerald-700' },
  { v: 'readmit_avoided',   l: 'Readmit avoided (close call)', tone: 'text-emerald-700' },
  { v: 'readmitted',        l: 'Readmitted', tone: 'text-red-700' },
  { v: 'lost_to_followup',  l: 'Lost to follow-up', tone: 'text-amber-700' },
  { v: 'deceased',          l: 'Deceased', tone: 'text-slate-700' },
];

const Tile = ({ label, value, tone, icon: Icon, testId }) => (
  <div className="cn-card rounded-2xl p-5" data-testid={testId}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tone}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="text-3xl font-display font-semibold text-cn-text">{value}</div>
    <div className="text-sm text-cn-text-muted mt-0.5">{label}</div>
  </div>
);

const WatchCard = ({ w, onOutcome, onEscalate }) => {
  const completed = (w.touchpoints || []).filter((t) => t.status === 'completed').length;
  const total = (w.touchpoints || []).length;
  const closed = w.status === 'closed';
  return (
    <div className="cn-card rounded-2xl p-5 space-y-3" data-testid={`spine-watch-card-${w.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${TIER_TONE[w.tier] || 'bg-slate-100 text-slate-700'}`} data-testid={`spine-watch-tier-${w.id}`}>
            {w.tier}
          </span>
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-cn-soft text-cn-primary">
            LACE {w.score}/19
          </span>
          {closed ? (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">Closed</span>
          ) : (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Active</span>
          )}
        </div>
        <span className="text-xs text-cn-text-muted">Day-30 {w.day_30_at ? new Date(w.day_30_at).toLocaleDateString() : '—'}</span>
      </div>
      <div className="text-sm text-cn-text">
        Patient <b>#{(w.patient_id || '').slice(0, 8)}</b> · <span className="text-cn-text-muted">touchpoints {completed}/{total}</span>
      </div>
      {w.outcome && (
        <div className="rounded-xl bg-cn-soft/40 border border-cn-border px-3 py-2 text-sm">
          Outcome: <b>{w.outcome}</b>
          {w.outcome_notes && <div className="text-xs text-cn-text-muted mt-0.5 italic">&ldquo;{w.outcome_notes}&rdquo;</div>}
        </div>
      )}
      {!closed && (
        <div className="flex gap-2 pt-2 border-t border-cn-border flex-wrap">
          <button
            onClick={() => onOutcome(w)}
            className="cn-btn cn-btn-primary !py-1.5 !px-3 text-xs"
            data-testid={`spine-watch-outcome-btn-${w.id}`}
          >
            <ClipboardCheck className="w-3.5 h-3.5" /> Record outcome
          </button>
          <button
            onClick={() => onEscalate(w)}
            className="cn-btn !bg-white !text-red-700 !border !border-red-200 hover:!bg-red-50 !py-1.5 !px-3 text-xs"
            data-testid={`spine-watch-escalate-btn-${w.id}`}
          >
            <ShieldAlert className="w-3.5 h-3.5" /> Escalate
          </button>
        </div>
      )}
    </div>
  );
};

const AlertRow = ({ a, onAck, busyId }) => (
  <div className="rounded-2xl border-l-4 border-red-400 bg-white px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`spine-alert-row-${a.id}`}>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-800">{a.status}</span>
        {(a.flags || []).map((f) => (
          <span key={f} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
            {f}
          </span>
        ))}
      </div>
      <div className="text-sm text-cn-text mt-1">
        Patient <b>#{(a.patient_id || '').slice(0, 8)}</b> · raised {new Date(a.raised_at).toLocaleString()}
      </div>
      {a.notes && <div className="text-xs text-cn-text-muted/80 mt-0.5 italic">&ldquo;{a.notes}&rdquo;</div>}
    </div>
    {a.status === 'open' && (
      <button
        disabled={busyId === a.id}
        onClick={() => onAck(a)}
        className="cn-btn cn-btn-primary !py-1.5 !px-3 text-xs"
        data-testid={`spine-alert-ack-btn-${a.id}`}
      >
        {busyId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5" /> Ack</>}
      </button>
    )}
  </div>
);

const OutcomeModal = ({ open, onClose, onConfirm, watch, busy }) => {
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  useEffect(() => { if (open) { setOutcome(''); setNotes(''); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="spine-outcome-modal">
        <DialogHeader>
          <DialogTitle>Record 30-day outcome</DialogTitle>
          <DialogDescription>
            Closes the watch. This feeds the readmission rate and CMS-aligned outcome metrics.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <div className="text-xs font-semibold text-cn-text-muted mb-1">Outcome</div>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="spine-outcome-select"><SelectValue placeholder="Select outcome" /></SelectTrigger>
              <SelectContent>
                {OUTCOMES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs font-semibold text-cn-text-muted mb-1">Notes (optional)</div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-xl" data-testid="spine-outcome-notes" />
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="cn-btn flex-1 justify-center" data-testid="spine-outcome-cancel">Cancel</button>
            <button
              disabled={busy || !outcome}
              onClick={() => onConfirm(watch, outcome, notes)}
              className="cn-btn cn-btn-primary flex-1 justify-center"
              data-testid="spine-outcome-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Close watch'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function SpineAdminWatches() {
  const { getAuthHeader } = useAuth();
  const [watches, setWatches] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [tierFilter, setTierFilter] = useState('');
  const [outcomeTarget, setOutcomeTarget] = useState(null);
  const [outcomeBusy, setOutcomeBusy] = useState(false);
  const [ackBusy, setAckBusy] = useState(null);
  const [sweepBusy, setSweepBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, aRes] = await Promise.all([
        axios.get(`${API}/spine/watches?status=${tab === 'closed' ? 'closed' : 'active'}${tierFilter ? `&tier=${tierFilter}` : ''}`, getAuthHeader()),
        axios.get(`${API}/spine/watch-alerts?status=open`, getAuthHeader()),
      ]);
      setWatches(Array.isArray(wRes.data) ? wRes.data : []);
      setAlerts(Array.isArray(aRes.data) ? aRes.data : []);
    } catch (e) {
      toast.error('Failed to load watches');
    } finally { setLoading(false); }
  }, [getAuthHeader, tab, tierFilter]);

  useEffect(() => { load(); }, [load]);

  const tierCounts = useMemo(() => {
    const obj = { low: 0, moderate: 0, high: 0, very_high: 0 };
    watches.forEach((w) => { obj[w.tier] = (obj[w.tier] || 0) + 1; });
    return obj;
  }, [watches]);

  const confirmOutcome = async (w, outcome, notes) => {
    setOutcomeBusy(true);
    try {
      await axios.post(`${API}/spine/watches/${w.id}/outcome`,
        { outcome, notes: notes || null }, getAuthHeader());
      toast.success('Outcome recorded — watch closed');
      setOutcomeTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record outcome');
    } finally { setOutcomeBusy(false); }
  };

  const handleEscalate = async (w) => {
    if (!window.confirm('Raise a manual ops escalation for this watch?')) return;
    try {
      await axios.post(`${API}/spine/watches/${w.id}/escalate`, {}, getAuthHeader());
      toast.success('Alert raised');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not escalate');
    }
  };

  const handleAck = async (a) => {
    setAckBusy(a.id);
    try {
      await axios.post(`${API}/spine/watch-alerts/${a.id}/ack`, {}, getAuthHeader());
      toast.success('Alert acknowledged');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not ack');
    } finally { setAckBusy(null); }
  };

  const runSweep = async () => {
    setSweepBusy(true);
    try {
      const res = await axios.post(`${API}/spine/watches/day30-sweep/tick`, {}, getAuthHeader());
      toast.success(`Day-30 sweep complete — closed ${res.data?.closed || 0} stale watch(es)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally { setSweepBusy(false); }
  };

  return (
    <DashboardShell
      title="Readmission watches"
      subtitle="30-day post-discharge oversight — decompensation alerts, ops escalations, and outcome capture."
      navItems={[]}
      testId="spine-admin-watches"
      rightSlot={
        <div className="flex items-center gap-2">
          <button onClick={load} className="cn-btn !bg-white !text-cn-primary !border !border-cn-border" data-testid="spine-watches-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            disabled={sweepBusy}
            onClick={runSweep}
            className="cn-btn cn-btn-primary"
            data-testid="spine-watches-day30-sweep-btn"
          >
            {sweepBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4" /> Run day-30 sweep</>}
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid={`spine-watch-stats-${tab}`}>
        <Tile label={`${tab === 'closed' ? 'Closed' : 'Active'} total`} value={watches.length} tone="bg-cn-soft text-cn-primary" icon={Activity} testId="spine-watch-stat-total" />
        <Tile label="Low" value={tierCounts.low} tone="bg-emerald-100 text-emerald-700" icon={ShieldCheck} testId="spine-watch-stat-low" />
        <Tile label="Moderate" value={tierCounts.moderate} tone="bg-amber-100 text-amber-700" icon={Clock} testId="spine-watch-stat-moderate" />
        <Tile label="High" value={tierCounts.high} tone="bg-orange-100 text-orange-700" icon={AlertTriangle} testId="spine-watch-stat-high" />
        <Tile label="Very high" value={tierCounts.very_high} tone="bg-red-100 text-red-700" icon={ShieldAlert} testId="spine-watch-stat-veryhigh" />
      </div>

      {/* Alerts queue */}
      <div className="mb-6">
        <h2 className="cn-display text-xl text-cn-text mb-3 flex items-center justify-between">
          <span className="inline-flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-600" /> Decompensation alerts</span>
          <span className="text-sm font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-3 py-1" data-testid="spine-alerts-count">
            {alerts.length} open
          </span>
        </h2>
        {alerts.length === 0 ? (
          <div className="cn-card rounded-2xl p-6 text-center text-cn-text-muted" data-testid="spine-alerts-empty">
            No open alerts.
          </div>
        ) : (
          <div className="space-y-2" data-testid="spine-alerts-list">
            {alerts.map((a) => <AlertRow key={a.id} a={a} onAck={handleAck} busyId={ackBusy} />)}
          </div>
        )}
      </div>

      {/* Filter + watches grid */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-cn-soft/50 border border-cn-border" data-testid="spine-watches-tabs">
          {['active', 'closed'].map((s) => (
            <button
              key={s}
              onClick={() => setTab(s)}
              data-testid={`spine-watches-tab-${s}`}
              className={`px-4 py-1.5 rounded-xl text-sm font-semibold transition-all ${tab === s ? 'bg-white shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-cn-soft/50 border border-cn-border" data-testid="spine-watches-tier-filter">
          {['', 'low', 'moderate', 'high', 'very_high'].map((t) => (
            <button
              key={t || 'all'}
              onClick={() => setTierFilter(t)}
              data-testid={`spine-watches-tier-${t || 'all'}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${tierFilter === t ? 'bg-white shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'}`}
            >
              {t ? t.replace('_', ' ') : 'All tiers'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>
      ) : watches.length === 0 ? (
        <div className="cn-card rounded-3xl p-10 text-center" data-testid="spine-watches-empty">
          <div className="w-14 h-14 rounded-2xl bg-cn-soft flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-7 h-7 text-cn-primary" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text mb-2">No watches</h3>
          <p className="text-cn-text-muted max-w-md mx-auto">
            Watches auto-spawn when a clinician accepts a placement and the org carries the readmission_watch entitlement.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="spine-watches-grid">
          {watches.map((w) => <WatchCard key={w.id} w={w} onOutcome={setOutcomeTarget} onEscalate={handleEscalate} />)}
        </div>
      )}

      <OutcomeModal
        open={!!outcomeTarget}
        onClose={() => setOutcomeTarget(null)}
        onConfirm={confirmOutcome}
        watch={outcomeTarget}
        busy={outcomeBusy}
      />
    </DashboardShell>
  );
}
